import { useRef } from 'react';
import { TextField } from '@components/base/inputs';
import { Button } from '@components/base/actions';
import { HStack, VStack } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';
import { Plus, Trash2 } from 'lucide-react';

interface RepeatableTextInputProps {
  readonly value: string[];
  readonly onChange: (value: string[]) => void;
  readonly label?: string;
  readonly placeholder?: string;
  readonly helperText?: string;
}

interface RepeatableItem {
  id: string;
  value: string;
}

let nextRepeatId = 0;
function generateRepeatId(): string {
  nextRepeatId += 1;
  return `rt-${nextRepeatId}-${Date.now()}`;
}

export function RepeatableTextInput({
  value,
  onChange,
  label,
  placeholder = 'Enter value',
  helperText,
}: Readonly<RepeatableTextInputProps>) {
  const itemsRef = useRef<RepeatableItem[]>([]);

  // Reconcile external value with internal items that carry stable IDs
  const externalItems = value.length > 0 ? value : [''];
  itemsRef.current = externalItems.map((v, i) => ({
    id: itemsRef.current[i]?.id ?? generateRepeatId(),
    value: v,
  }));
  const items = itemsRef.current;

  const handleChange = (index: number, newValue: string) => {
    const updated = items.map((item, i) => (i === index ? { ...item, value: newValue } : item));
    itemsRef.current = updated;
    onChange(updated.map((item) => item.value));
  };

  const handleAdd = () => {
    const newItems = [...items, { id: generateRepeatId(), value: '' }];
    itemsRef.current = newItems;
    onChange(newItems.map((item) => item.value));
  };

  const handleRemove = (index: number) => {
    if (items.length <= 1) {
      const reset = [{ id: generateRepeatId(), value: '' }];
      itemsRef.current = reset;
      onChange(['']);
      return;
    }
    const filtered = items.filter((_, i) => i !== index);
    itemsRef.current = filtered;
    onChange(filtered.map((item) => item.value));
  };

  return (
    <VStack gap={2}>
      {label && (
        <Text size="sm" weight="medium">
          {label}
        </Text>
      )}
      {items.map((item, index) => (
        <HStack key={item.id} gap={2} align="center">
          <TextField
            value={item.value}
            onChange={(e) => handleChange(index, e.target.value)}
            placeholder={placeholder}
            size="sm"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleRemove(index)}
            aria-label="Remove item"
          >
            <Icon icon={Trash2} size="sm" color="error" />
          </Button>
        </HStack>
      ))}
      <Button variant="ghost" size="sm" leftIcon={<Icon icon={Plus} size="sm" />} onClick={handleAdd}>
        Add
      </Button>
      {helperText && (
        <Text size="xs" color="muted">
          {helperText}
        </Text>
      )}
    </VStack>
  );
}
